import { useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/hooks/useAuth'
import { useLanguagePreference } from '@/hooks/useLanguagePreference'
import { Sidebar } from '@/navigation/Sidebar'
import { BuildInfoBar } from './BuildInfoBar'

function SidebarHeader() {
	return (
		<div className="px-4 py-4 border-b border-ds-border">
			<NavLink to="/dashboard" className="block">
				<div className="text-lg font-bold text-ds-text">TODO_APP_NAME</div>
				<div className="text-[10px] text-ds-text-subtle tracking-wide">TODO_APP_TAGLINE</div>
			</NavLink>
		</div>
	)
}

interface UserMenuProps {
	user: { displayName?: string; email: string; role: string }
	onSettings: () => void
	onLogout: () => void
}

function UserMenu({ user, onSettings, onLogout }: UserMenuProps) {
	// Profile claims can be absent (a federated IdP token may omit name/email),
	// so derive a safe display label + avatar initial that never throws.
	const displayLabel = user.displayName || user.email || 'Account'
	const initial = displayLabel.charAt(0).toUpperCase()

	return (
		<div className="border-t border-ds-border p-3">
			<DropdownMenu>
				<DropdownMenuTrigger className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm text-ds-text-muted hover:bg-ds-surface-muted focus:outline-none focus:ring-2 focus:ring-ds-ring cursor-pointer">
					<div className="w-8 h-8 rounded-full bg-ds-surface-muted flex items-center justify-center text-xs font-medium text-ds-text-muted">
						{initial}
					</div>
					<div className="flex-1 text-left min-w-0">
						<div className="text-sm font-medium text-ds-text truncate">{displayLabel}</div>
						<div className="text-xs text-ds-text-muted truncate">{user.role}</div>
					</div>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="start" side="top" className="w-56">
					<DropdownMenuLabel>
						<div className="flex flex-col">
							<span className="font-medium">{displayLabel}</span>
							{user.displayName && user.email && <span className="text-xs text-ds-text-muted">{user.email}</span>}
						</div>
					</DropdownMenuLabel>
					<DropdownMenuSeparator />
					<DropdownMenuItem onClick={onSettings}>Settings</DropdownMenuItem>
					<DropdownMenuSeparator />
					<DropdownMenuItem onClick={onLogout} className="text-ds-danger focus:text-ds-danger">
						Logout
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	)
}

interface SidebarToggleProps {
	collapsed: boolean
	onToggle: () => void
}

function SidebarToggle({ collapsed, onToggle }: SidebarToggleProps) {
	return (
		<button
			type="button"
			onClick={onToggle}
			className="fixed top-4 left-2 z-50 w-8 h-8 flex items-center justify-center rounded-md bg-ds-surface border border-ds-border shadow-sm text-ds-text-muted hover:text-ds-text hover:bg-ds-surface-muted transition-all duration-ds-base"
			style={{ left: collapsed ? '8px' : '216px' }}
			title={collapsed ? 'Show sidebar' : 'Hide sidebar'}
		>
			<svg
				className={`w-4 h-4 transition-transform duration-ds-base ${collapsed ? 'rotate-180' : ''}`}
				fill="none"
				stroke="currentColor"
				viewBox="0 0 24 24"
				aria-hidden="true"
			>
				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
			</svg>
		</button>
	)
}

/**
 * App shell.
 *
 * Navigation is rendered by the declarative `<Sidebar />` component, which
 * reads from `packages/frontend-app/src/navigation/config.ts` and filters
 * per viewer via `filterNav` + `useViewerRole`. Non-permitted items are
 * absent from the DOM (defense in depth).
 *
 * Downstream projects: rebrand the sidebar header (the `TODO_APP_NAME` /
 * `TODO_APP_TAGLINE` blocks below) and add ImpersonationBanner / dev-tool
 * affordances when those features are wired through your auth-service.
 */
export function Layout() {
	const { user, logout } = useAuth()
	// TO-197: the signed-in user's saved UI language follows them across devices.
	useLanguagePreference()
	const navigate = useNavigate()
	const [collapsed, setCollapsed] = useState(false)

	return (
		<div className="min-h-screen flex flex-col bg-ds-surface-muted">
			<div className="flex flex-1 min-h-0">
				<aside
					className={`bg-ds-surface border-r border-ds-border flex flex-col transition-all duration-ds-base ease-ds-in-out ${collapsed ? 'w-0 overflow-hidden opacity-0' : 'w-56 opacity-100'}`}
				>
					<SidebarHeader />
					<div className="flex-1 px-2 py-4 overflow-y-auto">
						<Sidebar />
					</div>
					<BuildInfoBar />
					{user && (
						<UserMenu user={user} onSettings={() => void navigate('/settings')} onLogout={() => void logout()} />
					)}
				</aside>

				<main className="flex-1 overflow-auto">
					<SidebarToggle collapsed={collapsed} onToggle={() => setCollapsed((prev) => !prev)} />
					<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
						<Outlet />
					</div>
				</main>
			</div>
		</div>
	)
}
